type StackRow = { values: { [key: string]: number } }
type StackBar = { index: number; key: string; y: number; height: number }

/**
 * Series ordered largest total first, so the biggest series forms the base of
 * every column. Ordering the series rather than each column keeps a series at
 * the same position bar to bar.
 */
export function keysByTotalDesc(rows: StackRow[]): string[] {
  const totals: Record<string, number> = {}
  rows.forEach((row) =>
    Object.entries(row.values).forEach(([key, value]) => {
      totals[key] = (totals[key] ?? 0) + Number(value)
    }),
  )
  return Object.keys(totals).sort((a, b) => totals[b] - totals[a])
}

/** The series whose segment caps each column: the topmost non-empty one. */
export function capKeysByIndex(barStacks: { bars: StackBar[] }[] | undefined): Record<number, string> {
  const capY: Record<number, number> = {}
  const capKey: Record<number, string> = {}
  barStacks?.forEach((barStack) =>
    barStack.bars?.forEach((bar) => {
      if (bar.height <= 0) return
      if (capY[bar.index] === undefined || bar.y < capY[bar.index]) {
        capY[bar.index] = bar.y
        capKey[bar.index] = bar.key
      }
    }),
  )
  return capKey
}

/** The series at the base of each column: the bottommost non-empty one. */
export function baseKeysByIndex(barStacks: { bars: StackBar[] }[] | undefined): Record<number, string> {
  const baseBottom: Record<number, number> = {}
  const baseKey: Record<number, string> = {}
  barStacks?.forEach((barStack) =>
    barStack.bars?.forEach((bar) => {
      if (bar.height <= 0) return
      const bottom = bar.y + bar.height
      if (baseBottom[bar.index] === undefined || bottom > baseBottom[bar.index]) {
        baseBottom[bar.index] = bottom
        baseKey[bar.index] = bar.key
      }
    }),
  )
  return baseKey
}

/** A column's values top of the stack first, the order the eye meets them. */
export function stackTopDown(keys: string[], values: { [key: string]: number }): [string, number][] {
  return [...keys]
    .reverse()
    .filter((key) => key in values)
    .map((key) => [key, values[key]])
}
